import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import {
  Sparkles,
  Shield,
  Lock,
  Puzzle,
  Zap,
  ArrowRight,
} from "lucide-react";

const tiles = [
  {
    icon: Shield,
    title: "Authentication",
    body: "Configure SSO providers (Entra ID, Google) and sign-in enforcement.",
    slug: "auth",
  },
  {
    icon: Lock,
    title: "Key Vault",
    body: "Per-tenant secrets: API keys, tokens, connection strings used by agents.",
    slug: "vault",
  },
  {
    icon: Puzzle,
    title: "MCP Servers",
    body: "Assign and allow-list the MCP servers agents in this tenant can use.",
    slug: "mcp",
  },
  {
    icon: Zap,
    title: "Skills",
    body: "Assign reusable skills/playbooks to this tenant's agents.",
    slug: "skills",
  },
] as const;

export function TenantSettingsOverview({
  onOpenSection,
}: {
  onOpenSection?: (section: "auth" | "vault" | "mcp" | "skills") => void;
}) {
  const { tenantId, onboardingComplete } = useTenant();
  const navigate = useNavigate();

  return (
    <div className="space-y-5">
      {/* Onboarding */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Sparkles size={18} />
              Onboarding
            </span>
            <Button
              size="sm"
              variant={onboardingComplete ? "outline" : "default"}
              onClick={() =>
                navigate(
                  `/tenants/${tenantId}/onboarding${onboardingComplete ? "?rerun=1" : ""}`,
                )
              }
            >
              <Sparkles size={14} className="mr-1.5" />
              {onboardingComplete ? "Re-run wizard" : "Run wizard"}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary">
            {onboardingComplete
              ? "Onboarding complete. Re-run the wizard to update company context, departments, and IT systems."
              : "Run the wizard to set up company context for your agents."}
          </p>
        </CardContent>
      </Card>

      {/* Tiles linking to sub-pages */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {tiles.map((t) => (
          <button
            key={t.slug}
            onClick={() => {
              if (onOpenSection) {
                onOpenSection(t.slug);
                return;
              }
              navigate(`/tenants/${tenantId}/settings/${t.slug}`);
            }}
            className="flex items-start gap-3 rounded-lg border border-border bg-surface p-4 text-left transition-colors hover:border-accent/40 hover:bg-surface-hover"
          >
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10">
              <t.icon size={18} className="text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-sm font-medium text-text-primary">
                {t.title}
                <ArrowRight size={12} className="text-text-muted" />
              </div>
              <p className="mt-1 text-xs text-text-muted">{t.body}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
